import { useCallback, useState } from 'react';
import { AvatarUpload } from '../components/avatar-upload';
import type { AppUser, AppUserDraft, City, Country } from '@domain/entities';
import type { AccountStatus } from '@domain/value-objects';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAuth } from '../providers/auth-context';
import { useAsync, useDebounced } from '../hooks/use-async';
import { useRealtimeVersion } from '../providers/realtime-context';
import { useIsMobile } from '../hooks/use-is-mobile';
import {
  ConfirmModal,
  Drawer,
  EmptyState,
  ErrorState,
  FilterPills,
  Modal,
  Pager,
  RefreshButton,
  SearchInput,
  StatusBadge,
  TableSkeleton,
  TextField,
} from '../components/ui';
import { PAGE_SIZE } from '../theme/tokens';

type StatusFilter = AccountStatus | 'all';

const ROLE_LABELS: Record<string, string> = {
  maman: 'Maman',
  papa: 'Papa',
  'grand-mere': 'Grand-mère',
  'grand-pere': 'Grand-père',
  tante: 'Tante',
  oncle: 'Oncle',
  'futur-parent': 'Futur parent',
  autre: 'Autre',
};

const FILTERS = [
  { value: 'all' as const, label: 'Toutes' },
  { value: 'active' as const, label: 'Actives' },
  { value: 'disabled' as const, label: 'Désactivées' },
];

export function UsersPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();
  const { can } = useAuth();
  const vBusinesses = useRealtimeVersion('businesses');
  const vMe = useRealtimeVersion('me');

  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounced(search);

  const [detail, setDetail] = useState<AppUser | null>(null);
  const [editing, setEditing] = useState<AppUser | null>(null);
  const [creating, setCreating] = useState(false);
  const emptyDraft: AppUserDraft = {
    name: '', email: '', phone: '', city: '', countryCode: '',
    role: '', avatarKey: '', dueDate: '', babySex: '', isFirstChild: null,
    age: null, wantsWeeklyTips: true, wantsLocalNews: true, locale: 'fr',
  };
  const [draft, setDraft] = useState<AppUserDraft>(emptyDraft);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const [pending, setPending] = useState<{ title: string; body: string; confirmLabel?: string; danger?: boolean; fn: () => void } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const countriesResource = useAsync(() => container.settings.listCountries.execute(), [container]);
  const citiesResource = useAsync(() => container.cities.list.execute(), [container]);
  const allCountries: Country[] = (countriesResource.data ?? []).filter((c) => c.isActive);
  const allCities: City[] = citiesResource.data ?? [];

  const resource = useAsync(
    () =>
      container.users.search.execute({
        status,
        search: debouncedSearch,
        page,
        pageSize: PAGE_SIZE,
      }),
    [container, status, debouncedSearch, page, vBusinesses, vMe],
  );

  const isMobile = useIsMobile();
  const rows = resource.data?.items ?? [];

  const toggleStatus = useCallback(
    async (user: AppUser) => {
      try {
        await container.users.setStatus.execute({
          id: user.id,
          status: user.status === 'active' ? 'disabled' : 'active',
        });
        toast.notify(user.status === 'active' ? 'Utilisatrice désactivée' : 'Utilisatrice réactivée');
        setDetail(null);
        resource.reload();
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast, resource],
  );

  const exportCsv = useCallback(async () => {
    try {
      const count = await container.users.exportCsv.execute();
      toast.notify(`${count} utilisatrices exportées`);
    } catch (error) {
      toast.notifyError(error);
    }
  }, [container, toast]);

  const openCreator = useCallback(() => {
    setDraft(emptyDraft);
    setAvatarPreview('');
    setSelectedCountryCode(allCountries[0]?.code ?? '');
    setCreating(true);
  }, [allCountries]);

  const openEditor = useCallback((user: AppUser) => {
    setDetail(null);
    setEditing(user);
    setAvatarPreview(user.avatarUrl);
    setDraft({
      name: user.name, email: user.email, phone: user.phone,
      city: user.city, countryCode: user.countryCode,
      role: user.role, avatarKey: '', dueDate: user.dueDate, babySex: user.babySex, isFirstChild: user.isFirstChild,
      age: user.age, wantsWeeklyTips: user.wantsWeeklyTips,
      wantsLocalNews: user.wantsLocalNews, locale: user.locale,
    });
    setSelectedCountryCode(user.countryCode ?? allCountries[0]?.code ?? '');
  }, [allCountries]);

  const deleteUser = useCallback(async (id: string) => {
    try {
      await container.users.remove.execute(id);
      toast.notify('Utilisateur supprimé');
      setDetail(null);
      resource.reload();
    } catch (error) {
      toast.notifyError(error);
    }
  }, [container, toast, resource]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      if (editing) {
        const saved = await container.users.update.execute({ id: editing.id, draft });
        if (avatarPreview && !saved.avatarUrl) saved.avatarUrl = avatarPreview;
        toast.notify('Utilisatrice mise à jour');
        setEditing(null);
      } else {
        const saved = await container.users.create.execute(draft);
        if (avatarPreview && !saved.avatarUrl) saved.avatarUrl = avatarPreview;
        toast.notify('Utilisatrice créée');
        setCreating(false);
      }
      resource.reload();
    } catch (error) {
      toast.notifyError(error);
    } finally {
      setSaving(false);
    }
  }, [container, editing, draft, avatarPreview, toast, resource]);

  return (
    <>
      <div className="bbm-toolbar">
        <div className="bbm-toolbar__start">
          <FilterPills
            options={FILTERS}
            value={status}
            onChange={(next) => {
              setStatus(next);
              setPage(1);
            }}
          />
          <SearchInput
            value={search}
            placeholder="Rechercher une utilisatrice…"
            onChange={(next) => {
              setSearch(next);
              setPage(1);
            }}
          />
        </div>
        <div className="bbm-toolbar__end">
          <button type="button" className="bbm-btn bbm-btn--primary" disabled={!can('users', 'create')} onClick={openCreator}>
            + Créer
          </button>
          <button
            type="button"
            className="bbm-btn bbm-btn--ghost"
            onClick={() => void exportCsv()}
          >
            Exporter (CSV)
          </button>
          <RefreshButton onRefresh={resource.reload} loading={resource.loading} />
        </div>
      </div>

      {resource.loading ? <TableSkeleton /> : null}
      {resource.error ? (
        <ErrorState
          status={resource.error.status}
          message={resource.error.message}
          onRetry={resource.reload}
        />
      ) : null}

      {!resource.loading && !resource.error && rows.length === 0 ? (
        <EmptyState
          title="Aucun résultat"
          body="Aucune utilisatrice ne correspond à cette recherche."
          actionLabel="Réinitialiser"
          onAction={() => {
            setStatus('all');
            setSearch('');
            setPage(1);
          }}
        />
      ) : null}

      {!resource.loading && !resource.error && rows.length > 0 && resource.data ? (
        <>
          {isMobile ? (
            <div className="bbm-card-stack">
              {rows.map((user) => (
                <div key={user.id} className="bbm-mobile-card">
                  <div className="bbm-mobile-card__head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                      {user.avatarUrl ? (
                        <img src={user.avatarUrl} alt={user.initials} style={{ width: 32, height: 32, borderRadius: 99, objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div aria-hidden="true" style={{ width: 32, height: 32, borderRadius: 99, background: '#7FB3D5', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                          {user.initials}
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div className="bbm-mobile-card__title">{user.name}</div>
                        <div className="bbm-mobile-card__sub">{user.email || user.phone}</div>
                      </div>
                    </div>
                    <StatusBadge status={user.status === 'active' ? 'approved' : 'disabled'} label={user.status === 'active' ? 'Actif' : 'Désactivé'} />
                  </div>
                  <div className="bbm-mobile-card__fields">
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Ville</span>
                      <span>{user.city || '—'}</span>
                    </div>
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Rôle</span>
                      <span>{ROLE_LABELS[user.role] ?? '—'}</span>
                    </div>
                    <div className="bbm-mobile-card__field">
                      <span className="bbm-mobile-card__label">Inscrite</span>
                      <span>{new Date(user.registeredAt).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  <div className="bbm-mobile-card__actions">
                    <button type="button" className="bbm-btn" onClick={() => setDetail(user)}>Voir</button>
                    <button type="button" className="bbm-btn" disabled={!can('users', 'edit')} onClick={() => openEditor(user)}>Modifier</button>
                    <button type="button" className="bbm-btn bbm-btn--danger" disabled={!can('users', 'delete')} onClick={() => setPending({
                      title: 'Supprimer cet utilisateur ?',
                      body: 'Action irréversible. Toutes les données seront définitivement supprimées.',
                      confirmLabel: 'Supprimer',
                      danger: true,
                      fn: () => void deleteUser(user.id),
                    })}>Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bbm-panel">
              <div className="bbm-row bbm-row--head">
                <div style={{ width: 32 }} />
                <div style={{ width: 160 }}>Nom</div>
                <div style={{ width: 110 }}>Rôle</div>
                <div style={{ width: 160 }}>Email</div>
                <div style={{ width: 90 }}>Ville</div>
                <div style={{ width: 100 }}>Grossesse</div>
                <div style={{ width: 100 }}>Date acc.</div>
                <div style={{ width: 40 }}>Âge</div>
                <div style={{ width: 60 }}>Sexe</div>
                <div style={{ width: 70 }}>1er enf.</div>
                <div style={{ width: 90 }}>Statut</div>
                <div style={{ width: 100 }}>Inscrite le</div>
                <div style={{ marginLeft: 'auto' }}>Actions</div>
              </div>

              {rows.map((user) => (
                <div key={user.id} className="bbm-row">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.initials}
                      style={{ width: 32, height: 32, borderRadius: 99, objectFit: 'cover', flexShrink: 0 }}
                    />
                  ) : (
                    <div
                      aria-hidden="true"
                      style={{
                        width: 32, height: 32, borderRadius: 99,
                        background: '#7FB3D5', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 800, flexShrink: 0,
                      }}
                    >
                      {user.initials}
                    </div>
                  )}
                  <div className="bbm-cell-strong" style={{ width: 160 }}>
                    {user.name}
                  </div>
                  <div className="bbm-cell" style={{ width: 110 }}>
                    {ROLE_LABELS[user.role] ?? '—'}
                  </div>
                  <div className="bbm-cell bbm-truncate" style={{ width: 160 }}>
                    {user.email}
                  </div>
                  <div className="bbm-cell" style={{ width: 90 }}>
                    {user.city || '—'}
                  </div>
                  <div className="bbm-cell" style={{ width: 100 }}>
                    {user.pregnancy || '—'}
                  </div>
                  <div className="bbm-cell" style={{ width: 100 }}>
                    {user.dueDate
                      ? new Date(user.dueDate).toLocaleDateString('fr-FR')
                      : '—'}
                  </div>
                  <div className="bbm-cell" style={{ width: 40 }}>
                    {user.age != null ? user.age : '—'}
                  </div>
                  <div className="bbm-cell" style={{ width: 60 }}>
                    {user.babySex === 'garcon' ? 'Garçon' : user.babySex === 'fille' ? 'Fille' : '—'}
                  </div>
                  <div className="bbm-cell" style={{ width: 70 }}>
                    {user.isFirstChild === true ? 'Oui' : user.isFirstChild === false ? 'Non' : '—'}
                  </div>
                  <div style={{ width: 90 }}>
                    <StatusBadge
                      status={user.status === 'active' ? 'approved' : 'disabled'}
                      label={user.status === 'active' ? 'Actif' : 'Désactivé'}
                    />
                  </div>
                  <div className="bbm-cell" style={{ width: 100 }}>
                    {new Date(user.registeredAt).toLocaleDateString('fr-FR')}
                  </div>
                  <div className="bbm-row__actions">
                    <button type="button" className="bbm-btn" onClick={() => setDetail(user)}>
                      Voir
                    </button>
                    <button type="button" className="bbm-btn" disabled={!can('users', 'edit')} onClick={() => openEditor(user)}>
                      Modifier
                    </button>
                    <button
                      type="button"
                      className="bbm-btn bbm-btn--danger"
                      disabled={!can('users', 'delete')}
                      onClick={() => setPending({
                        title: 'Supprimer cet utilisateur ?',
                        body: 'Action irréversible. Toutes les données seront définitivement supprimées.',
                        confirmLabel: 'Supprimer',
                        danger: true,
                        fn: () => void deleteUser(user.id),
                      })}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Pager
            page={resource.data.page}
            pageCount={resource.data.pageCount}
            total={resource.data.total}
            pageSize={resource.data.pageSize}
            onChange={setPage}
          />
        </>
      ) : null}

      {detail ? (
        <Drawer title={detail.name} subtitle={detail.city} onClose={() => setDetail(null)}>
          {detail.avatarUrl && (
            <img src={detail.avatarUrl} alt={detail.name}
              style={{ width: 64, height: 64, borderRadius: 99, objectFit: 'cover', marginBottom: 12 }} />
          )}
          <StatusBadge
            status={detail.status === 'active' ? 'approved' : 'disabled'}
            label={detail.status === 'active' ? 'Actif' : 'Désactivé'}
          />
          <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div className="bbm-detail-label">Rôle</div>
              <div className="bbm-detail-value">{ROLE_LABELS[detail.role] ?? '—'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Email</div>
              <div className="bbm-detail-value">{detail.email || '—'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Téléphone</div>
              <div className="bbm-detail-value">{detail.phone || '—'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Suivi de grossesse</div>
              <div className="bbm-detail-value">{detail.pregnancy || '—'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Date d'accouchement prévue</div>
              <div className="bbm-detail-value">{detail.dueDate || '—'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Sexe du bébé</div>
              <div className="bbm-detail-value">
                {detail.babySex === 'garcon' ? 'Garçon' : detail.babySex === 'fille' ? 'Fille' : '—'}
              </div>
            </div>
            <div>
              <div className="bbm-detail-label">Premier enfant</div>
              <div className="bbm-detail-value">
                {detail.isFirstChild === true ? 'Oui' : detail.isFirstChild === false ? 'Non' : '—'}
              </div>
            </div>
            <div>
              <div className="bbm-detail-label">Âge</div>
              <div className="bbm-detail-value">{detail.age != null ? `${detail.age} ans` : '—'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Langue</div>
              <div className="bbm-detail-value">{detail.locale === 'en' ? 'English' : 'Français'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Conseils hebdomadaires</div>
              <div className="bbm-detail-value">{detail.wantsWeeklyTips ? 'Activés' : 'Désactivés'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Actualités locales</div>
              <div className="bbm-detail-value">{detail.wantsLocalNews ? 'Activées' : 'Désactivées'}</div>
            </div>
            <div>
              <div className="bbm-detail-label">Inscrite le</div>
              <div className="bbm-detail-value">{detail.registeredAt}</div>
            </div>
          </div>
          <div style={{ marginTop: 26, display: 'flex', gap: 10 }}>
            <button
              type="button"
              className="bbm-btn bbm-btn--block"
              disabled={!can('users', 'edit')}
              onClick={() => openEditor(detail)}
            >
              Modifier
            </button>
            <button
              type="button"
              className="bbm-btn bbm-btn--block bbm-btn--danger"
              disabled={!can('users', 'delete')}
              title={can('users', 'delete') ? '' : 'Réservé au super-admin'}
              onClick={() => {
                if (detail.status === 'active') {
                  setPending({
                    title: 'Désactiver ce compte ?',
                    body: "L'utilisatrice ne pourra plus se connecter.",
                    confirmLabel: 'Désactiver',
                    danger: true,
                    fn: () => void toggleStatus(detail),
                  });
                } else {
                  setPending({
                    title: 'Réactiver ce compte ?',
                    body: "L'utilisatrice pourra à nouveau se connecter.",
                    confirmLabel: 'Réactiver',
                    fn: () => void toggleStatus(detail),
                  });
                }
              }}
            >
              {detail.status === 'active' ? 'Désactiver le compte' : 'Réactiver le compte'}
            </button>
          </div>
          <button
            type="button"
            className="bbm-btn bbm-btn--danger bbm-btn--block"
            style={{ marginTop: 10 }}
            disabled={!can('users', 'delete')}
            title={can('users', 'delete') ? '' : 'Réservé au super-admin'}
            onClick={() => setPending({
              title: 'Supprimer définitivement cet utilisateur ?',
              body: 'Action irréversible. Toutes les données seront définitivement supprimées.',
              confirmLabel: 'Supprimer',
              danger: true,
              fn: () => void deleteUser(detail.id),
            })}
          >
            Supprimer le compte
          </button>
        </Drawer>
      ) : null}

      {pending !== null ? (
        <ConfirmModal
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          danger={pending.danger}
          busy={actionBusy}
          onConfirm={() => {
            setActionBusy(true);
            Promise.resolve(pending.fn()).finally(() => {
              setActionBusy(false);
              setPending(null);
            });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}

      {(creating || editing) ? (
        <Modal
          title={creating ? 'Créer une utilisatrice' : "Modifier l'utilisatrice"}
          width={600}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSubmit={() => void save()}
          busy={saving}
        >
          <AvatarUpload
            currentUrl={avatarPreview}
            purpose="avatar"
            onUpload={(key, previewUrl) => {
              setDraft((d) => ({ ...d, avatarKey: key }));
              setAvatarPreview(previewUrl);
            }}
            onRequestUpload={container.media.requestUpload}
          />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 0 : '0 16px' }}>
            <TextField
              label="Nom"
              value={draft.name}
              onChange={(value) => setDraft({ ...draft, name: value })}
            />
            <TextField
              label="Email"
              type="email"
              value={draft.email}
              onChange={(value) => setDraft({ ...draft, email: value })}
            />
            <TextField
              label="Téléphone"
              value={draft.phone}
              onChange={(value) => setDraft({ ...draft, phone: value })}
            />
            <TextField
              label="Âge"
              type="number"
              value={draft.age ?? ''}
              onChange={(v) => setDraft((d) => ({ ...d, age: v === '' ? null : Number(v) }))}
            />
            <div style={{ marginBottom: 16 }}>
              <label className="bbm-label">Pays</label>
              <select
                className="bbm-input"
                value={selectedCountryCode}
                onChange={(e) => {
                  setSelectedCountryCode(e.target.value);
                  setDraft((d) => ({ ...d, countryCode: e.target.value, city: '' }));
                }}
              >
                {allCountries.map((c) => (
                  <option key={c.id} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="bbm-label">Ville</label>
              <select
                className="bbm-input"
                value={draft.city}
                onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
              >
                <option value="">— choisir —</option>
                {allCities
                  .filter((c) => !selectedCountryCode || c.countryCode === selectedCountryCode)
                  .filter((c) => c.isActive)
                  .map((c) => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="bbm-label">Rôle</label>
              <select
                className="bbm-input"
                value={draft.role}
                onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value }))}
              >
                <option value="">— Non renseigné —</option>
                {Object.entries(ROLE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="bbm-label">Langue</label>
              <select
                className="bbm-input"
                value={draft.locale}
                onChange={(e) => setDraft((d) => ({ ...d, locale: e.target.value }))}
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="bbm-label">Date d'accouchement prévue</label>
              <input
                type="date"
                className="bbm-input"
                value={draft.dueDate}
                onChange={(e) => setDraft((d) => ({ ...d, dueDate: e.target.value }))}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="bbm-label">Sexe du bébé</label>
              <select
                className="bbm-input"
                value={draft.babySex}
                onChange={(e) => setDraft((d) => ({ ...d, babySex: e.target.value }))}
              >
                <option value="">— Non renseigné —</option>
                <option value="garcon">Garçon</option>
                <option value="fille">Fille</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="bbm-label">Premier enfant</label>
              <select
                className="bbm-input"
                value={draft.isFirstChild === true ? 'true' : draft.isFirstChild === false ? 'false' : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setDraft((d) => ({
                    ...d,
                    isFirstChild: v === 'true' ? true : v === 'false' ? false : null,
                  }));
                }}
              >
                <option value="">— Non renseigné —</option>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="bbm-label">Conseils hebdomadaires</label>
              <select
                className="bbm-input"
                value={draft.wantsWeeklyTips ? 'true' : 'false'}
                onChange={(e) => setDraft((d) => ({ ...d, wantsWeeklyTips: e.target.value === 'true' }))}
              >
                <option value="true">Activés</option>
                <option value="false">Désactivés</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="bbm-label">Actualités locales</label>
              <select
                className="bbm-input"
                value={draft.wantsLocalNews ? 'true' : 'false'}
                onChange={(e) => setDraft((d) => ({ ...d, wantsLocalNews: e.target.value === 'true' }))}
              >
                <option value="true">Activées</option>
                <option value="false">Désactivées</option>
              </select>
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
